import { useMemo, useEffect, useRef, useState, useCallback } from 'react';
import { CheckCircle2, RotateCcw } from 'lucide-react';
import { cn } from '@/lib/utils';

type Message = {
  type: 'user' | 'genie';
  text: string;
  delay: number;
};

type ScenarioId = 'emergency' | 'product_inquiry' | 'support' | 'detailed_message' | 'multilingual';

// Generic scenarios - intelligent voicemail that captures info and answers questions
// Timing: faster entry (messages appear quicker) but stay visible longer within 15s window
const SCENARIOS: Record<ScenarioId, { title: string; messages: Message[] }> = {
  emergency: {
    title: 'Emergency Calls',
    messages: [
      { type: 'user', text: "I have a pipe burst and there's water everywhere!", delay: 300 },
      { type: 'genie', text: "I understand this is urgent. Let me get your details so our team can call you back right away.", delay: 1200 },
      { type: 'user', text: "Okay, please hurry", delay: 2500 },
      { type: 'genie', text: "Of course. What's your name and the best number to reach you?", delay: 3400 },
      { type: 'user', text: "John Smith, 555-234-5678", delay: 4600 },
      { type: 'genie', text: "Got it, John. I've marked this as urgent - you'll receive a callback within 15 minutes.", delay: 5500 },
    ],
  },
  product_inquiry: {
    title: 'Product Inquiries',
    messages: [
      { type: 'user', text: "I'm looking at your premium plan - what's included?", delay: 300 },
      { type: 'genie', text: "Great question! The premium plan includes 24/7 support, priority response, and dedicated account management.", delay: 1200 },
      { type: 'user', text: "What's the pricing?", delay: 2500 },
      { type: 'genie', text: "Premium starts at $99 per month. Would you like someone to walk you through the options?", delay: 3400 },
      { type: 'user', text: "Yes, that would be helpful", delay: 4600 },
      { type: 'genie', text: "Perfect. What's your name and the best time to reach you tomorrow?", delay: 5500 },
    ],
  },
  support: {
    title: 'Support Questions',
    messages: [
      { type: 'user', text: "How do I reset my password?", delay: 300 },
      { type: 'genie', text: "I can help with that. Go to the login page and click 'Forgot Password' - you'll get a reset link by email.", delay: 1200 },
      { type: 'user', text: "I tried that but didn't get the email", delay: 2500 },
      { type: 'genie', text: "It may have gone to spam. If not there, I'll have our team follow up. What's your email address?", delay: 3400 },
      { type: 'user', text: "sarah@example.com", delay: 4600 },
      { type: 'genie', text: "Thanks Sarah. Someone will reach out within a few hours to help resolve this.", delay: 5500 },
    ],
  },
  detailed_message: {
    title: 'Detailed Messages',
    messages: [
      { type: 'user', text: "Hi, I wanted to leave a message about my recent service", delay: 300 },
      { type: 'genie', text: "Of course. Can I get your name and the best way to reach you?", delay: 1200 },
      { type: 'user', text: "Mike Johnson, 555-876-5432", delay: 2500 },
      { type: 'genie', text: "Thanks Mike. What would you like us to know about your service?", delay: 3400 },
      { type: 'user', text: "The technician was great but I have a follow-up question about the warranty", delay: 4600 },
      { type: 'genie', text: "Got it - warranty question noted. Our team will call you tomorrow. Morning or afternoon better?", delay: 5500 },
    ],
  },
  multilingual: {
    title: 'Multilingual',
    messages: [
      { type: 'user', text: "Hola, tengo una pregunta sobre sus servicios", delay: 300 },
      { type: 'genie', text: "¡Hola! Con gusto te ayudo. ¿Qué te gustaría saber?", delay: 1200 },
      { type: 'user', text: "¿Cuáles son sus horarios de atención?", delay: 2500 },
      { type: 'genie', text: "Nuestro horario es de lunes a viernes, 8am a 6pm. ¿Te gustaría que alguien te llame?", delay: 3400 },
      { type: 'user', text: "Sí, por favor", delay: 4600 },
      { type: 'genie', text: "Perfecto. ¿Cuál es tu nombre y número de teléfono?", delay: 5500 },
    ],
  },
};


// Simplified outcomes - just title and check
type SimpleOutcome = {
  id: string;
  text: string;
  delay: number;
};

// Outcomes appear after conversation finishes (~6s), with faster cascade
const SCENARIO_OUTCOMES_SIMPLE: Record<ScenarioId, SimpleOutcome[]> = {
  emergency: [
    { id: '1', text: 'Contact details captured', delay: 6500 },
    { id: '2', text: 'Issue severity assessed', delay: 6700 },
    { id: '3', text: 'Marked as urgent (P1)', delay: 6900 },
    { id: '4', text: 'Callback scheduled within 15 min', delay: 7100 },
    { id: '5', text: 'Customer reassured', delay: 7300 },
  ],
  product_inquiry: [
    { id: '1', text: 'Lead information captured', delay: 6500 },
    { id: '2', text: 'Product questions answered', delay: 6700 },
    { id: '3', text: 'Pricing details shared', delay: 6900 },
    { id: '4', text: 'Follow-up call scheduled', delay: 7100 },
    { id: '5', text: 'Potential value: $1,188/yr', delay: 7300 },
  ],
  support: [
    { id: '1', text: 'Self-service steps provided', delay: 6500 },
    { id: '2', text: 'Issue documented', delay: 6700 },
    { id: '3', text: 'Email address collected', delay: 6900 },
    { id: '4', text: 'Support ticket created', delay: 7100 },
    { id: '5', text: 'Follow-up scheduled', delay: 7300 },
  ],
  detailed_message: [
    { id: '1', text: 'Full message recorded', delay: 6500 },
    { id: '2', text: 'Contact details captured', delay: 6700 },
    { id: '3', text: 'Context gathered', delay: 6900 },
    { id: '4', text: 'Preferred callback time set', delay: 7100 },
    { id: '5', text: 'Agent time saved: 5 min', delay: 7300 },
  ],
  multilingual: [
    { id: '1', text: 'Language detected: Spanish', delay: 6500 },
    { id: '2', text: 'Business hours shared', delay: 6700 },
    { id: '3', text: 'Contact details captured', delay: 6900 },
    { id: '4', text: 'Callback requested', delay: 7100 },
    { id: '5', text: 'Native language experience', delay: 7300 },
  ],
};


const SCENARIO_IDS: ScenarioId[] = ['emergency', 'product_inquiry', 'support', 'detailed_message', 'multilingual'];
const SCENARIO_DURATION = 15000;
const INITIAL_DELAY = 3000;
const FULL_LOOP_DURATION = INITIAL_DELAY + (SCENARIO_IDS.length * SCENARIO_DURATION);

export const RealWorldScenarios = () => {
  const sectionRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isInView, setIsInView] = useState(false);
  const [chatFaded, setChatFaded] = useState(false);
  const [timeIntoScenario, setTimeIntoScenario] = useState(0);
  const [scenarioIndex, setScenarioIndex] = useState(0);
  const [scenarioStarted, setScenarioStarted] = useState(false);

  // Sync timing to video
  useEffect(() => {
    const video = videoRef.current;
    if (!video || !isInView) {
      setScenarioStarted(false);
      setScenarioIndex(0);
      setTimeIntoScenario(0);
      return;
    }

    let rafId: number;

    const syncToVideo = () => {
      const videoTimeMs = video.currentTime * 1000;
      const loopTime = videoTimeMs % FULL_LOOP_DURATION;

      if (loopTime < INITIAL_DELAY) {
        setScenarioStarted(false);
        setScenarioIndex(0);
        setTimeIntoScenario(0);
        setChatFaded(false);
      } else {
        const timeIntoScenarios = loopTime - INITIAL_DELAY;
        const newScenarioIndex = Math.min(
          Math.floor(timeIntoScenarios / SCENARIO_DURATION),
          SCENARIO_IDS.length - 1
        );
        const timeIntoCurrentScenario = timeIntoScenarios % SCENARIO_DURATION;

        setScenarioStarted(true);
        setScenarioIndex(newScenarioIndex);
        setTimeIntoScenario(timeIntoCurrentScenario);
      }

      rafId = requestAnimationFrame(syncToVideo);
    };

    rafId = requestAnimationFrame(syncToVideo);

    return () => cancelAnimationFrame(rafId);
  }, [isInView]);

  const handleReplay = () => {
    if (videoRef.current) {
      videoRef.current.currentTime = 0;
      videoRef.current.play().catch(() => {});
    }
    setIsInView(false);
    setChatFaded(false);
    setTimeout(() => setIsInView(true), 50);
  };

  const handleCardsStart = useCallback(() => {
    setChatFaded(true);
  }, []);

  // Handle scroll
  useEffect(() => {
    const handleScroll = () => {
      if (!sectionRef.current) return;

      const rect = sectionRef.current.getBoundingClientRect();
      const windowHeight = window.innerHeight;

      const progress = Math.max(
        0,
        Math.min(1, (windowHeight - rect.top) / (windowHeight * 0.8))
      );

      const nowInView = progress > 0.3;
      if (nowInView && !isInView) {
        setIsInView(true);
        setChatFaded(false);
      } else if (!nowInView && isInView) {
        setIsInView(false);
        setChatFaded(false);
      }

      if (videoRef.current) {
        if (progress > 0.3) {
          videoRef.current.play().catch(() => {});
        } else {
          videoRef.current.pause();
        }
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();

    return () => window.removeEventListener('scroll', handleScroll);
  }, [isInView]);

  const currentScenarioId = SCENARIO_IDS[scenarioIndex];
  const currentScenario = SCENARIOS[currentScenarioId];
  const outcomes = SCENARIO_OUTCOMES_SIMPLE[currentScenarioId];

  const visibleMessages = useMemo(() => {
    return currentScenario.messages.filter(msg => timeIntoScenario >= msg.delay);
  }, [currentScenario.messages, timeIntoScenario]);

  const isTyping = useMemo(() => {
    return currentScenario.messages.some(msg =>
      msg.type === 'genie' &&
      timeIntoScenario >= msg.delay - 1000 &&
      timeIntoScenario < msg.delay
    );
  }, [currentScenario.messages, timeIntoScenario]);

  const visibleCardIds = useMemo(() => {
    return outcomes
      .filter(card => timeIntoScenario >= card.delay)
      .map(card => card.id);
  }, [outcomes, timeIntoScenario]);

  // Notify when cards start appearing
  useEffect(() => {
    if (visibleCardIds.length > 0 && !chatFaded) {
      handleCardsStart();
    }
  }, [visibleCardIds.length, chatFaded, handleCardsStart]);

  return (
    <section
      ref={sectionRef}
      className="relative w-full overflow-hidden pt-16 lg:pt-24 pb-16 bg-white"
    >
      {/* Hidden video for timing sync */}
      <video
        ref={videoRef}
        src="/hero-image/After Hours HG animation.mp4"
        className="hidden"
        loop
        muted
        playsInline
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12 lg:mb-16">
          <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-bold text-gray-900 mb-5 leading-tight tracking-tight">
            <span className="block">Never miss another opportunity</span>
            <span className="block">even when you're closed</span>
          </h2>
          <p className="text-lg md:text-xl lg:text-2xl text-gray-500 max-w-3xl mx-auto">
            After Hours captures every call, answers questions, and ensures customers feel heard — not ignored.
          </p>
        </div>

        {/* Three column layout: Tabs | Chat | Outcomes */}
        <div className="grid grid-cols-1 lg:grid-cols-[200px_1fr_280px] gap-8 lg:gap-10 items-start">
          {/* Left column - Scenario tabs stacked vertically */}
          <div className="flex flex-col gap-2">
            {SCENARIO_IDS.map((id, index) => (
              <button
                key={id}
                onClick={() => {
                  if (videoRef.current) {
                    videoRef.current.currentTime = (INITIAL_DELAY + index * SCENARIO_DURATION) / 1000;
                  }
                }}
                className={cn(
                  'px-4 py-3 rounded-lg text-sm font-medium transition-all text-left',
                  scenarioIndex === index
                    ? 'bg-gradient-to-r from-purple-600 to-blue-600 text-white shadow-lg'
                    : 'bg-white text-gray-600 hover:bg-gray-50 border border-gray-200'
                )}
              >
                {SCENARIOS[id].title}
              </button>
            ))}

            {/* Replay button */}
            <button
              onClick={handleReplay}
              className="mt-2 flex items-center gap-1.5 text-gray-400 hover:text-gray-600 transition-colors text-xs"
              aria-label="Replay scenario"
            >
              <RotateCcw size={14} />
              <span>Replay</span>
            </button>
          </div>

          {/* Center column - Chat bubbles */}
          <div
            className={cn(
              'min-h-[480px] flex flex-col justify-start transition-opacity duration-300',
              chatFaded ? 'opacity-60' : 'opacity-100'
            )}
            style={{ opacity: isInView ? 1 : 0.5 }}
          >
            {isInView && scenarioStarted && (
              <div className="flex flex-col gap-3">
                {visibleMessages.map((message, index) => (
                  <div
                    key={`${currentScenarioId}-${index}`}
                    className={cn(
                      'flex items-end gap-2',
                      message.type === 'user' ? 'justify-end' : 'justify-start',
                      'animate-fade-slide-in'
                    )}
                  >
                    {/* Genie avatar for genie messages */}
                    {message.type === 'genie' && (
                      <div className="flex-shrink-0 w-9 h-9 rounded-full bg-white shadow-md overflow-hidden">
                        <img
                          src="/genie-mascots/role/support.png"
                          alt="Genie"
                          className="w-full h-full object-cover"
                        />
                      </div>
                    )}
                    <div
                      className={cn(
                        'px-4 py-3 rounded-2xl shadow-lg backdrop-blur-sm',
                        'text-sm lg:text-base leading-relaxed',
                        message.type === 'user'
                          ? 'bg-white text-gray-800 rounded-br-sm border border-gray-100 max-w-[85%]'
                          : 'bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-bl-sm max-w-[calc(85%-40px)]'
                      )}
                    >
                      {message.text}
                    </div>
                  </div>
                ))}

                {/* Typing indicator */}
                {isTyping && (
                  <div className="flex items-end gap-2 justify-start animate-fade-slide-in">
                    <div className="flex-shrink-0 w-9 h-9 rounded-full bg-white shadow-md overflow-hidden">
                      <img
                        src="/genie-mascots/role/support.png"
                        alt="Genie"
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div className="px-4 py-3 rounded-2xl rounded-bl-sm bg-gradient-to-r from-purple-600 to-blue-600 shadow-lg">
                      <div className="flex gap-1.5">
                        <span className="w-2 h-2 bg-white/70 rounded-full animate-bounce" style={{ animationDelay: '0ms', animationDuration: '600ms' }} />
                        <span className="w-2 h-2 bg-white/70 rounded-full animate-bounce" style={{ animationDelay: '150ms', animationDuration: '600ms' }} />
                        <span className="w-2 h-2 bg-white/70 rounded-full animate-bounce" style={{ animationDelay: '300ms', animationDuration: '600ms' }} />
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Placeholder when not started */}
            {(!isInView || !scenarioStarted) && (
              <div className="flex flex-col gap-3 opacity-30">
                <div className="flex justify-end">
                  <div className="px-4 py-3 rounded-2xl rounded-br-sm bg-gray-100 text-gray-400 max-w-[80%]">
                    Customer message appears here...
                  </div>
                </div>
                <div className="flex justify-start">
                  <div className="px-4 py-3 rounded-2xl rounded-bl-sm bg-gray-200 text-gray-400 max-w-[80%]">
                    Genie response appears here...
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Right column - Simple outcome checklist */}
          <div style={{ opacity: isInView ? 1 : 0.5 }}>
            {isInView && scenarioStarted && (
              <div className="flex flex-col gap-2.5">
                {outcomes.map((outcome) => {
                  const isVisible = visibleCardIds.includes(outcome.id);

                  return (
                    <div
                      key={`${currentScenarioId}-${outcome.id}`}
                      className={cn(
                        'flex items-center gap-3 px-4 py-3 rounded-xl bg-white border border-gray-100 shadow-sm transition-all duration-400 ease-out',
                        isVisible
                          ? 'opacity-100 translate-x-0'
                          : 'opacity-0 translate-x-4 pointer-events-none'
                      )}
                    >
                      <CheckCircle2 className="w-5 h-5 text-purple-600 flex-shrink-0" />
                      <span className="text-gray-700 text-sm">{outcome.text}</span>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Placeholder when not started */}
            {(!isInView || !scenarioStarted) && (
              <div className="flex flex-col gap-2.5 opacity-30">
                {[1, 2, 3, 4, 5].map((i) => (
                  <div key={i} className="flex items-center gap-3 px-4 py-3 rounded-xl bg-gray-100">
                    <div className="w-5 h-5 rounded-full bg-gray-200" />
                    <div className="h-4 w-40 bg-gray-200 rounded" />
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      <style>{`
        @keyframes fade-slide-in {
          0% {
            opacity: 0;
            transform: translateY(8px);
          }
          100% {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .animate-fade-slide-in {
          animation: fade-slide-in 0.25s ease-out forwards;
        }
        @keyframes card-glow {
          0% {
            box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
          }
          50% {
            box-shadow: 0 20px 40px -5px rgba(147, 51, 234, 0.25), 0 8px 16px -4px rgba(59, 130, 246, 0.15);
          }
          100% {
            box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
          }
        }
        .animate-card-glow {
          animation: card-glow 1.2s ease-out;
        }
        @keyframes shimmer {
          0% {
            background-position: 200% 0;
          }
          100% {
            background-position: -200% 0;
          }
        }
        .animate-shimmer {
          animation: shimmer 3s ease-in-out infinite;
        }
      `}</style>
    </section>
  );
};
